import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { FacetItem, Facets as FacetData, SearchFacet, TreeFacet, TreeNode, YearBin } from '../api'
import { api } from '../api'
import type { Group } from '../tree'
import { findCondition, setYearRange, toWire, toggleFacet, yearRange } from '../tree'

type Props = {
  facets: FacetData | undefined
  tree: Group
  setTree: (t: Group | ((p: Group) => Group)) => void
  open: boolean
  isResearcher: boolean
}

const STEP = 8            // values shown per list / tree level before "mehr"
const MORE = 20           // added per click on "mehr"

export function Facets({ facets, tree, setTree, open, isResearcher }: Props) {
  const [personMode, setPersonMode] = useState<'all' | 'any'>('all')
  const toggle = (field: string, item: { id: number; label: string }, not = false) =>
    setTree((t) => toggleFacet(t, field, item.id, item.label, not, field === 'person' && personMode === 'any'))
  const state = (field: string, id: number) => {
    const c = findCondition(tree, field, id)
    return c ? (c.not ? 'off' : 'on') : ''
  }
  const qkey = useMemo(() => JSON.stringify(toWire(tree)), [tree])
  const common = { state, toggle, tree, qkey }

  if (!facets) return <aside className={`facets${open ? ' open' : ''}`} aria-label="Filter"><p className="note">Filter werden geladen …</p></aside>

  const unnamed = facets.gesicht.filter((f) => f.unnamed)
  const more = facets.more ?? {}
  return (
    <aside className={`facets${open ? ' open' : ''}`} aria-label="Filter">
      <Box title="Zeitraum">
        <Histogram bins={facets.jahr} range={yearRange(tree)} onRange={(r) => setTree((t) => setYearRange(t, r))} />
      </Box>
      <Box title="Personen" search={more.person ? 'Alle Personen durchsuchen …' : 'Filtern …'} extra={
        <label className="mode muted">auf dem Bild:{' '}
          <select value={personMode} onChange={(e) => setPersonMode(e.target.value as 'all' | 'any')} aria-label="Personen verknüpfen">
            <option value="all">alle zusammen</option>
            <option value="any">mindestens eine</option>
          </select>
        </label>}>
        {(filter) => <Flat items={facets.person} field="person" remote={!!more.person} filter={filter} {...common} />}
      </Box>
      <Box title="Unbenannte Gesichter">
        {() => unnamed.length ? (
          <>
            <div className="faces">
              {unnamed.slice(0, 16).map((f) => (
                <button key={f.id} className={`face ${state('gesicht', f.id)}`} title={`${f.label} – ${f.count} Treffer`} onClick={() => toggle('gesicht', f)}>
                  <img src={`/api/faces/${f.id}`} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = "hidden" }} />
                  <span>#{f.label.replace(/\D/g, '')} · {f.count}</span>
                </button>
              ))}
            </div>
            <p className="note">Gesichter, die die KI wiedererkennt, aber noch niemand benannt hat. Wählen, um alle Aufnahmen dieser Person zu sehen.</p>
          </>
        ) : <p className="note">In dieser Auswahl keine unbenannten Gesichter.</p>}
      </Box>
      <Box title="Orte" search="Orte suchen …">
        {(f) => <Tree facet="ort" field="ort" nodes={facets.ort} total={facets.roots?.ort ?? 0} filter={f} autoOpen {...common} />}
      </Box>
      <Box title="Schlagwörter" search="Schlagwörter suchen …">
        {(f) => <Tree facet="schlagwort" field="schlagwort" nodes={facets.schlagwort} total={facets.roots?.schlagwort ?? 0} filter={f} {...common} />}
      </Box>
      <Box title="Sammlungen" search="Sammlungen suchen …">
        {(f) => <Tree facet="sammlung" field="sammlung" nodes={facets.sammlung} total={facets.roots?.sammlung ?? 0} filter={f} {...common} />}
      </Box>
      <Box title="Technik / Material" search={facets.technik.length > STEP ? 'Filtern …' : undefined}>
        {(f) => <Flat items={facets.technik} field="technik" remote={!!more.technik} filter={f} {...common} max={6} />}
      </Box>
      <Box title="Fotograf/in" search={facets.fotograf.length > STEP ? 'Filtern …' : undefined}>
        {(f) => <Flat items={facets.fotograf} field="fotograf" remote={!!more.fotograf} filter={f} {...common} max={6} />}
      </Box>
      {isResearcher && facets.plattform.length > 0 && (
        <Box title="Plattform-Tags">{() => <Flat items={facets.plattform} field="plattform" {...common} />}</Box>
      )}
    </aside>
  )
}

function Box({ title, children, extra, search }: {
  title: string; extra?: React.ReactNode; search?: string
  children: React.ReactNode | ((filter: string) => React.ReactNode)
}) {
  const [filter, setFilter] = useState('')
  return (
    <section className="facet">
      <header><h3>{title}</h3>{extra}</header>
      {search && (
        <div className="ffilter-wrap">
          <input className="ffilter" type="search" placeholder={search} value={filter} onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setFilter('') }} aria-label={`${title} durchsuchen`} />
          {filter && <button className="ffilter-x" onClick={() => setFilter('')} aria-label="Suche leeren">×</button>}
        </div>
      )}
      {typeof children === 'function' ? children(filter) : children}
    </section>
  )
}

type Common = {
  field: string
  filter?: string
  state: (field: string, id: number) => string
  toggle: (field: string, item: { id: number; label: string }, not?: boolean) => void
  tree: Group
  qkey: string
}

function Row({ item, field, state, toggle, caret, onCaret, path, children }: Pick<Common, 'field' | 'state' | 'toggle'> & {
  item: FacetItem; caret?: 'open' | 'closed'; onCaret?: () => void; path?: string; children?: React.ReactNode
}) {
  return (
    <li className={state(field, item.id)}>
      {caret
        ? <button className="tw" onClick={onCaret} aria-expanded={caret === 'open'} aria-label={`${item.label} ${caret === 'open' ? 'zuklappen' : 'aufklappen'}`}>{caret === 'open' ? '▾' : '▸'}</button>
        : <span className="tw" aria-hidden="true" />}
      <button className="nm" title={path ? `${path} › ${item.label}` : item.label} onClick={() => toggle(field, item)}>
        {item.label}{path && <span className="path">{path}</span>}
      </button>
      <span className="ct">{item.count ? item.count.toLocaleString('de-DE') : ''}</span>
      <button className="ex" title="Ausschließen" aria-label={`${item.label} ausschließen`} onClick={() => toggle(field, item, true)}>⊖</button>
      {children}
    </li>
  )
}

/** Values of this field chosen in the query, so they stay visible (and removable) whatever the lists show. */
function useSelected(tree: Group, field: string) {
  return useMemo(() => {
    const out: { id: number; label: string }[] = []
    const walk = (g: Group) => g.items.forEach((i) => {
      if ('items' in i) walk(i)
      else if ('field' in i && i.field === field && typeof i.value === 'number') out.push({ id: i.value, label: i.label ?? `#${i.value}` })
    })
    walk(tree)
    return out
  }, [tree, field])
}

function useDebouncedValue<T>(value: T, ms: number) {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

/** Server-side search over all values of a facet in the current result. */
function Found({ facet, field, filter = '', state, toggle, tree, qkey }: Common & { facet: SearchFacet }) {
  const text = useDebouncedValue(filter.trim(), 220)
  const q = useQuery({
    queryKey: ['facet-search', facet, qkey, text],
    queryFn: ({ signal }) => api.facetSearch(tree, facet, text, signal),
    enabled: text.length >= 2, staleTime: 60_000, placeholderData: keepPreviousData,
  })
  if (filter.trim().length < 2) return <p className="note">Mindestens zwei Buchstaben …</p>
  const items = q.data ?? []
  return (
    <ul className={`fl found${q.isFetching ? ' busy' : ''}`}>
      {items.map((it) => (
        <Row key={it.id} item={it} field={field} state={state} toggle={toggle} path={it.path?.length > 1 ? it.path.slice(0, -1).join(' › ') : undefined} />
      ))}
      {!items.length && !q.isFetching && <li className="muted"><span /><span>nichts gefunden</span></li>}
      {items.length >= 30 && <li className="muted small"><span /><span>… genauer suchen für weitere</span></li>}
    </ul>
  )
}

function Flat({ items, field, remote = false, filter = '', max = STEP, ...rest }: Omit<Common, 'field' | 'filter'> & {
  items: FacetItem[]; field: string; remote?: boolean; filter?: string; max?: number
}) {
  const [shown, setShown] = useState(max)
  const selected = useSelected(rest.tree, field)
  if (remote && filter.trim()) return <Found facet={field as SearchFacet} field={field} filter={filter} {...rest} />
  let list = items
  for (const s of selected) if (!list.some((x) => x.id === s.id)) list = [{ ...s, count: 0 }, ...list]
  if (filter) list = list.filter((x) => x.label.toLowerCase().includes(filter.toLowerCase()))
  const visible = filter ? list : list.slice(0, shown)
  return (
    <>
      <ul className="fl">
        {visible.length ? visible.map((it) => <Row key={it.id} item={it} field={field} state={rest.state} toggle={rest.toggle} />)
          : <li className="muted"><span /><span>keine</span></li>}
      </ul>
      {!filter && (list.length > shown || shown > max) && (
        <div className="more-row">
          {list.length > shown && <button className="linkbtn more" onClick={() => setShown(shown + MORE)}>mehr ({list.length - shown}{remote ? '+' : ''})</button>}
          {shown > max && <button className="linkbtn more" onClick={() => setShown(max)}>weniger</button>}
          {remote && list.length <= shown && <span className="note">weitere über die Suche</span>}
        </div>
      )}
    </>
  )
}

/** Hierarchical facet: one level at a time; carets only where the server says there are children. */
function Tree({ facet, field, nodes, total, filter = '', autoOpen = false, ...rest }: Omit<Common, 'field' | 'filter'> & {
  facet: TreeFacet; field: string; nodes: TreeNode[]; total: number; filter?: string; autoOpen?: boolean
}) {
  // nodes of the search response: the biggest roots (+ for places the children of the first root)
  const initial = useMemo(() => {
    const m = new Map<number | null, TreeNode[]>()
    for (const n of nodes) {
      const p = n.parent != null && nodes.some((x) => x.id === n.parent) ? n.parent : null
      if (!m.has(p)) m.set(p, [])
      m.get(p)!.push(n)
    }
    return m
  }, [nodes])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const touched = useRef(false)
  useEffect(() => {
    if (touched.current || !autoOpen) return
    const first = initial.get(null)?.[0]
    setExpanded(first && initial.has(first.id) ? new Set([first.id]) : new Set())
  }, [initial, autoOpen])
  const selected = useSelected(rest.tree, field)

  if (filter.trim()) return <Found facet={facet} field={field} filter={filter} {...rest} />

  const roots = initial.get(null) ?? []
  const pinned = selected.filter((s) => !roots.some((r) => r.id === s.id))
  const toggleOpen = (id: number) => {
    touched.current = true
    setExpanded((e) => { const n = new Set(e); if (n.has(id)) n.delete(id); else n.add(id); return n })
  }
  return (
    <>
      {pinned.length > 0 && (
        <ul className="fl pinned">{pinned.map((s) => <Row key={s.id} item={{ ...s, count: 0 }} field={field} state={rest.state} toggle={rest.toggle} />)}</ul>
      )}
      <Level facet={facet} field={field} parent={null} preset={roots} total={total} expanded={expanded} toggleOpen={toggleOpen} initial={initial} {...rest} />
    </>
  )
}

function Level({ facet, field, parent, preset, total, expanded, toggleOpen, initial, state, toggle, tree, qkey }: Omit<Common, 'field' | 'filter'> & {
  facet: TreeFacet; field: string; parent: number | null; preset?: TreeNode[]; total?: number
  expanded: Set<number>; toggleOpen: (id: number) => void; initial: Map<number | null, TreeNode[]>
}) {
  const [shown, setShown] = useState(STEP)
  const enough = !!preset && (preset.length >= shown || (total != null && preset.length >= total))
  const q = useQuery({
    queryKey: ['facet-children', facet, qkey, parent, shown],
    queryFn: ({ signal }) => api.facetChildren(tree, facet, parent, shown, signal),
    enabled: !enough, staleTime: 60_000, placeholderData: keepPreviousData,
  })
  const items = enough ? preset! : q.data?.items ?? preset ?? []
  const count = enough ? total ?? preset!.length : q.data?.total ?? total ?? items.length
  if (!items.length) return q.isLoading ? <p className="note">Lade …</p> : parent == null ? <ul className="fl"><li className="muted"><span /><span>keine</span></li></ul> : null
  return (
    <ul className={`fl${q.isFetching ? ' busy' : ''}`}>
      {items.slice(0, shown).map((n) => {
        const open = expanded.has(n.id)
        return (
          <Row key={n.id} item={n} field={field} state={state} toggle={toggle}
            caret={n.has_children ? (open ? 'open' : 'closed') : undefined} onCaret={() => toggleOpen(n.id)}>
            {open && n.has_children && (
              <Level facet={facet} field={field} parent={n.id} preset={initial.get(n.id)} expanded={expanded} toggleOpen={toggleOpen}
                initial={initial} state={state} toggle={toggle} tree={tree} qkey={qkey} />
            )}
          </Row>
        )
      })}
      {(count > shown || shown > STEP) && (
        <li className="more-row">
          <span />
          <span>
            {count > shown && <button className="linkbtn more" onClick={() => setShown(shown + MORE)}>mehr ({(count - shown).toLocaleString('de-DE')})</button>}
            {shown > STEP && <button className="linkbtn more" onClick={() => setShown(STEP)}>weniger</button>}
          </span>
        </li>
      )}
    </ul>
  )
}

// --- time histogram ----------------------------------------------------------------

function Histogram({ bins, range, onRange }: { bins: YearBin[]; range: { from?: number; to?: number } | null; onRange: (r: { from: number; to: number } | null) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null)
  const start = bins[0]?.from ?? 1900
  const end = (bins[bins.length - 1]?.to ?? 2029) + 1

  const yearAt = (clientX: number) => {
    const r = canvas.current!.getBoundingClientRect()
    const x = Math.min(Math.max(clientX - r.left, 0), r.width - 1)
    return Math.floor(start + (x / r.width) * (end - start))
  }

  useEffect(() => {
    const c = canvas.current
    if (!c) return
    const draw = () => {
      const w = c.clientWidth || 260, h = 86, dpr = window.devicePixelRatio || 1
      c.width = w * dpr; c.height = h * dpr
      const g = c.getContext('2d')!
      g.scale(dpr, dpr)
      const cs = getComputedStyle(document.documentElement)
      const tok = (n: string) => cs.getPropertyValue(n).trim()
      const max = Math.max(1, ...bins.map((b) => b.count_all))
      const top = 6, bottom = h - 14, span = end - start
      const sel = drag ? { from: Math.min(drag.a, drag.b), to: Math.max(drag.a, drag.b) } : range
      if (sel && (sel.from || sel.to)) {
        const x0 = ((sel.from ?? start) - start) / span * w, x1 = ((sel.to ?? end - 1) + 1 - start) / span * w
        g.fillStyle = tok('--mark'); g.globalAlpha = .55; g.fillRect(x0, 0, Math.max(2, x1 - x0), bottom); g.globalAlpha = 1
      }
      bins.forEach((b) => {
        const x = (b.from - start) / span * w, bw = (b.to - b.from + 1) / span * w
        const ha = b.count_all / max * (bottom - top), hr = b.count / max * (bottom - top)
        g.fillStyle = tok('--rule'); g.fillRect(x + .5, bottom - ha, Math.max(1, bw - 1.5), ha)
        g.fillStyle = tok('--ink'); g.fillRect(x + .5, bottom - hr, Math.max(1, bw - 1.5), hr)
      })
      g.fillStyle = tok('--muted'); g.font = `10px ${tok('--f-mono')}`; g.textBaseline = 'top'; g.textAlign = 'center'
      ;[1920, 1945, 1970, 1990, 2010].forEach((y) => {
        const x = (y - start) / span * w
        g.fillRect(x, bottom, 1, 3)
        g.fillText(String(y), Math.min(Math.max(x, 14), w - 14), bottom + 3)
      })
    }
    draw()
    const ro = new ResizeObserver(draw)
    ro.observe(c)
    const mo = new MutationObserver(draw)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => { ro.disconnect(); mo.disconnect() }
  }, [bins, range, drag, start, end])

  const label = drag ? `${Math.min(drag.a, drag.b)}–${Math.max(drag.a, drag.b)}`
    : range ? (range.from === range.to ? `${range.from}` : `${range.from ?? ''}–${range.to ?? ''}`) : 'ziehen zum Eingrenzen'

  return (
    <>
      <canvas ref={canvas} className="hist" height={86} aria-label="Zeitraum wählen: mit der Maus ziehen"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); const y = yearAt(e.clientX); setDrag({ a: y, b: y }) }}
        onPointerMove={(e) => { if (drag) setDrag({ ...drag, b: yearAt(e.clientX) }) }}
        onPointerUp={() => {
          if (!drag) return
          const a = Math.min(drag.a, drag.b), b = Math.max(drag.a, drag.b)
          setDrag(null)
          if (a === b && range) onRange(null)
          else onRange({ from: a, to: b })
        }} />
      <div className="histcap"><span>{start}</span><span>{label}</span><span>{end - 1}</span></div>
      {range && <button className="linkbtn more" onClick={() => onRange(null)}>Zeitraum aufheben</button>}
    </>
  )
}
